import PropTypes from "prop-types";
import {
  createContext,
  useEffect,
  useReducer,
  useCallback,
  useMemo,
} from "react";
// config
import { useApolloClient } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import { CURRENT_USER_QUERY } from "./graphql";
import { setSession } from "./utils";
import fcmService from "../../services/fcmService";

// ----------------------------------------------------------------------// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === "INITIAL") {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === "LOGIN") {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === "UPDATE_USER") {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === "LOGOUT") {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const apolloClient = useApolloClient();
  const {
    loginWithRedirect,
    isAuthenticated,
    getIdTokenClaims,
    logout: Auth0Logout,
  } = useAuth0();

  const getUser = useCallback(async () => {
    const { data, error } = await apolloClient.query({
      query: CURRENT_USER_QUERY,
      errorPolicy: "all",
    });

    // const user = await auth0Client.getUser();
    const { user } = data || {};

    return extractUser(user);
  }, [apolloClient]);

  const initialize = useCallback(async () => {
    try {
      if (isAuthenticated) {
        const user = await getUser();

        dispatch({
          type: "INITIAL",
          payload: {
            isAuthenticated: true,
            user,
          },
        });

        // Initialize FCM for the user
        // Note: We don't pass navigate here because AuthProvider is outside Router
        // FCM service will use window.location.href as fallback
        if (user?.id) {
          try {
            await fcmService.initializeForUser(user.id, apolloClient);
          } catch (fcmError) {
            console.error('Error initializing FCM:', fcmError);
          }
        }
      } else {
        dispatch({
          type: "INITIAL",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: "INITIAL",
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [getUser, isAuthenticated, apolloClient]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN - pass returnTo so after callback we navigate back to the same page (e.g. on refresh)
  const login = useCallback(async (returnTo) => {
    await loginWithRedirect({
      appState: { returnTo: returnTo || window.location.pathname || "/" },
    });
  }, [loginWithRedirect]);

  // AUTHORIZE THROUGH SPA
  const authorize = useCallback(async () => {
    const claim = await getIdTokenClaims();
    const accessToken = claim.__raw;
    setSession(accessToken);

    if (isAuthenticated) {
      const user = await getUser();
      if (user?.access_token_data) {
        localStorage.setItem("user", JSON.stringify(user));
        user.access_token_data.outlook_access_token &&
          localStorage.setItem(
            "outlook_access_token",
            user.access_token_data.outlook_access_token
          );
        user.access_token_data.outlook_refresh_token &&
          localStorage.setItem(
            "outlook_refresh_token",
            user.access_token_data.outlook_refresh_token
          );
        user.access_token_data.google_access_token &&
          localStorage.setItem(
            "google_access_token",
            user.access_token_data.google_access_token
          );
        user.access_token_data.google_refresh_token &&
          localStorage.setItem(
            "google_refresh_token",
            user.access_token_data.google_refresh_token
          );
        user.access_token_data.sms_access_token &&
          localStorage.setItem(
            "ring_central_token",
            user.access_token_data.sms_access_token
          );
        user.access_token_data.sms_refresh_token &&
          localStorage.setItem(
            "ring_central_refresh_token",
            user.access_token_data.sms_refresh_token
          );
        user.access_token_data.sms_user_data &&
          localStorage.setItem(
            "ring_central_user_data",
            JSON.stringify(user.access_token_data.sms_user_data)
          );
      }
      dispatch({
        type: "AUTHORIZE",
        payload: {
          isAuthenticated: true,
          user,
        },
      });

      // Initialize FCM for the user after authorization
      // Note: We don't pass navigate here because AuthProvider is outside Router
      // FCM service will use window.location.href as fallback
      if (user?.id) {
        try {
          await fcmService.initializeForUser(user.id, apolloClient);
        } catch (fcmError) {
          console.error('Error initializing FCM after authorization:', fcmError);
        }
      }
    }
  }, [getIdTokenClaims, getUser, isAuthenticated, apolloClient]);

  const refreshUser = useCallback(async () => {
    if (!isAuthenticated) return;
    const user = await getUser();
    if (user) {
      dispatch({ type: "UPDATE_USER", payload: { user } });
    }
  }, [getUser, isAuthenticated]);

  // LOGOUT
  const logout = useCallback(async () => {
    // Delete FCM tokens before logging out
    if (state.user?.id) {
      try {
        await fcmService.deleteUserTokens(state.user.id, apolloClient);
      } catch (fcmError) {
        console.error('Error deleting FCM tokens on logout:', fcmError);
      }
    }
    
    await Auth0Logout();
  }, [Auth0Logout, state.user, apolloClient]);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: "auth0",
      login,
      logout,
      authorize,
      refreshUser,
    }),
    [
      state.isInitialized,
      state.isAuthenticated,
      state.user,
      login,
      logout,
      authorize,
      refreshUser,
    ]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export const extractUser = (x) => {
  if (!x) {
    return null;
  }

  const user = {
    id: x.id,
    avatarUrl: x?.avatar?.downloadUrl,
    firstName: x.firstName,
    lastName: x.lastName,
    name: `${x.firstName} ${x.lastName}`,
    email: x.email,
    origin: x.origin,
    status: x.status,
    roles: x.roles?.items || [],
    createdAt: x.createdAt,
    updatedAt: x.updatedAt,
    userTimezone: x.userTimezone || "America/Los_Angeles",
  };

  return user;
};

